// ViewContract.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { getContractById } from '@store/HR/Person/contractsSlice';
import Loading from '@components/Shared/Loader/Loading';
import { useAppDispatch, useAppSelector } from '@store/hooks';

interface ViewContractProps {
  contractId: any;
  onClose: () => void;
}

const ViewContract: React.FC<ViewContractProps> = ({ contractId, onClose }) => {
  //   const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  const { currentContract, loadingDetails, errorDetails } = useAppSelector(state => state.PersonContracts);

 
  useEffect(() => {
    if (contractId) {
      dispatch(getContractById(contractId));
    }
  }, [contractId, dispatch]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-success';
      case 'expired':
        return 'bg-warning';
      case 'terminated':
        return 'bg-danger';
      case 'suspended':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'نشط';
      case 'expired':
        return 'منتهي';
      case 'terminated':
        return 'ملغي';
      case 'suspended':
        return 'موقوف';
      default:
        return status;
    }
  };

  return (
    <Loading loading={loadingDetails} error={errorDetails}>
      <div className='p-3'>
        {!currentContract && (
          <div className='alert alert-warning'>
            <div className='d-flex align-items-center'>
              <i className='fa fa-info-circle fa-lg me-3'></i>
              <div>
                <h6 className='alert-heading mb-1'>لا توجد بيانات</h6>
                <p className='mb-0'>لم يتم العثور على بيانات العقد</p>
              </div>
            </div>
          </div>
        )}

        <div className='row'>
          <div className='col-xl-12'>
            <div className='card shadow-sm border-0'>
              <div className='card-header bg-theme text-white py-3'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div className='d-flex align-items-center'>
                    <i className='fa fa-file-contract fa-lg me-2'></i>
                    <h5 className='mb-0'>تفاصيل العقد</h5>
                  </div>
                  {currentContract && (
                    <span className={`badge ${getStatusBadge(currentContract.contractStatus)} fs-6`}>
                      {getStatusText(currentContract.contractStatus)}
                    </span>
                  )}
                </div>
              </div>

              <div className='card-body p-0'>
                <div className='table-responsive'>
                  <table className='table table-hover mb-0'>
                    <tbody>
                      <tr className='align-middle'>
                        <th className='bg-secondary bg-opacity-40 text-theme fw-semibold' style={{ width: '200px' }}>
                          <div className='d-flex align-items-center'>
                            <i className='fa fa-hashtag fa-fw me-2'></i>
                            رقم العقد
                          </div>
                        </th>
                        <td className='fw-medium'>{currentContract?.contractNumber}</td>
                      </tr>

                      <tr className='align-middle'>
                        <th className='bg-secondary bg-opacity-40 text-theme fw-semibold'>
                          <div className='d-flex align-items-center'>
                            <i className='fa fa-tag fa-fw me-2'></i>
                            نوع العقد
                          </div>
                        </th>
                        <td>{currentContract?.contractType}</td>
                      </tr>

                      <tr className='align-middle'>
                        <th className='bg-secondary bg-opacity-40 text-theme fw-semibold'>
                          <div className='d-flex align-items-center'>
                            <i className='fa fa-calendar-plus fa-fw me-2'></i>
                            تاريخ البداية
                          </div>
                        </th>
                        <td>
                          <span className='badge bg-primary bg-opacity-10 text-primary px-3 py-2'>
                            {currentContract?.startDate?.split('T')[0]}
                          </span>
                        </td>
                      </tr>

                      <tr className='align-middle'>
                        <th className='bg-secondary bg-opacity-40 text-theme fw-semibold'>
                          <div className='d-flex align-items-center'>
                            <i className='fa fa-calendar-minus fa-fw me-2'></i>
                            تاريخ النهاية
                          </div>
                        </th>
                        <td>
                          <span className='badge bg-primary bg-opacity-10 text-primary px-3 py-2'>
                            {currentContract?.endDate?.split('T')[0]}
                          </span>
                        </td>
                      </tr>

                      <tr className='align-middle'>
                        <th className='bg-secondary bg-opacity-40 text-theme fw-semibold'>
                          <div className='d-flex align-items-center'>
                            <i className='fa fa-calendar-check fa-fw me-2'></i>
                            تاريخ التوقيع
                          </div>
                        </th>
                        <td>
                          <span className='badge bg-success bg-opacity-10 text-success px-3 py-2'>
                            {currentContract?.signedDate?.split('T')[0]}
                          </span>
                        </td>
                      </tr>

                      <tr className='align-middle'>
                        <th className='bg-secondary bg-opacity-40 text-theme fw-semibold'>
                          <div className='d-flex align-items-center'>
                            <i className='fa fa-user-check fa-fw me-2'></i>
                            الموقع
                          </div>
                        </th>
                        <td className='fw-medium'>{currentContract?.signedBy}</td>
                      </tr>

                      <tr className='align-middle'>
                        <th className='bg-secondary bg-opacity-40 text-theme fw-semibold'>
                          <div className='d-flex align-items-center'>
                            <i className='fa fa-umbrella-beach fa-fw me-2'></i>
                            الإجازة السنوية
                          </div>
                        </th>
                        <td>
                          <span className='badge bg-info px-3 py-2 fs-6'>{currentContract?.annualLeave} يوم</span>
                        </td>
                      </tr>

                      <tr className='align-middle'>
                        <th className='bg-secondary bg-opacity-40 text-theme fw-semibold'>
                          <div className='d-flex align-items-center'>
                            <i className='fa fa-check-circle fa-fw me-2'></i>
                            حالة الاعتماد
                          </div>
                        </th>
                        <td>
                          <span
                            className={`badge ${currentContract?.approvedFlag ? 'bg-success' : 'bg-warning'} px-3 py-2`}
                          >
                            {currentContract?.approvedFlag ? 'معتمد' : 'غير معتمد'}
                          </span>
                        </td>
                      </tr>

                      <tr className='align-middle'>
                        <th className='bg-secondary bg-opacity-40 text-theme fw-semibold'>
                          <div className='d-flex align-items-center'>
                            <i className='fa fa-sticky-note fa-fw me-2'></i>
                            الوصف
                          </div>
                        </th>
                        <td>
                          {currentContract?.description ? (
                            <div className='bg-secondary bg-opacity-40 rounded p-3 border'>
                              {currentContract.description}
                            </div>
                          ) : (
                            <span className='text-muted'>لا توجد ملاحظات</span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Loading>
  );
};

export default ViewContract;
